import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { inquiryType, gtmEventType, contactType, bidderStatus, phoneType, LinkRelationTypes, invalidCurrencyPopUpBaseCatIDs, maxSecondsAuctionTimeListingCanBeExtendedDueToBidding } from '@Components/Utilities/SandhillsConstants';
import { getDistinct, gtmPushEvent, sandhillsRequest, truncateString, setScrollPosition, unsetScrollPosition, logSAMError } from '@Components/Utilities/SandhillsUtility';
import ListingGridImage from '@Components/Listings/ListingGridImage';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import { loadable } from '@Components/Utilities/SandhillsLoadable';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import translator from '@Components/Utilities/Translator';
import TranslationText from '@Components/Utilities/TranslationText';
import ready from 'document-ready';
import Countdown from 'react-countdown';

const WidgetList = loadable(() => import('@Components/Shared/Widgets/WidgetList'));
const FloorPlan = loadable(() => import('@Components/Listings/FloorPlan'));
const RentalAndLeasePrices = loadable(() => import('@Components/Shared/RentalAndLeasePrices'));
const EmployeeListingModal = loadable(() => import('@Components/Shared/Modals/EmployeeListingModal'), true);
const ListingBiddingBox = loadable(() => import('@Components/Listings/ListingBiddingBox'));
const BadgeList = loadable(() => import('@Components/Shared/BadgeList'));
const WatchlistExpansion = loadable(() => import('@Components/Shared/Listings/WatchlistsExpansion'));
const OptionsPopper = loadable(() => import('@Components/Shared/Listings/OptionsPopper'));
const FleetEvaluatorValues = loadable(() => import('@Components/Shared/FleetEvaluatorValues'));
const ListingDisplayLocation = loadable(() => import('@Components/Shared/Listings/ListingDisplayLocation'));
const ListingDetailLink = loadable(() => import('@Components/Shared/Listings/ListingDetailLink'));
const ShippingEstimate = loadable(() => import('@Components/Shared/ShippingEstimate'));
const PaymentsAsLowAsLink = loadable(() => import('@Components/Shared/FinancialCalculator/PaymentsAsLowAsLink'));

const WebListing_GridCard = (props) => {
    const currentUser = useRecoilValue(atoms.currentUser);
    const currentSite = useRecoilValue(atoms.currentWebsite);
    const windowSize = useWindowSize();
    const [showEmployeeModal, setShowEmployeeModal] = useState(false);
    const linkTarget = props.openDetailPageInNewTab ? '_blank' : '_self';
    const ref = useRef([]);
    const [userWatchlist, setUserWatchlist] = useState(props.UserWatchlist);
    const [unsaveWatchlist, setUnsaveWatchlist] = useState(false);
    const [savedToWatchlist, setSavedToWatchlist] = useState(props.IsWatchlist && !props.IsRecommendedListingListSearch ? props.IsWatchlist : false);
    const [displayWatchlistExpansion, setDisplayWatchlistExpansion] = useState(false);
    const routes = useRecoilValue(atoms.routes);
    const UnauthorizedAuctionResultRequest = props.IsAuctionResult && !currentUser?.IsAuthenticated && props.LoginLink;
    const hasSAM = props.ShowSendAMessage || props.ShowCheckAvailability;
    const isLivestockHayStaw = (props.BaseCategoryId == invalidCurrencyPopUpBaseCatIDs.Livestock || props.BaseCategoryId == invalidCurrencyPopUpBaseCatIDs.HayStraw);
    const priceDesc = isLivestockHayStaw ? props.Specs?.filter((s) => s.Key == 'Price Description') : '';
    const [showExpandedSpecs, setShowExpandedSpecs] = useState(false);
    const LSMSpecStartIndex = isLivestockHayStaw && props.Specs.length > 0 && priceDesc.length > 0 && !(props.IsAT || props.IsEF) ? 0 : 1;
    const efColor = '#ffcc01';
    const atColor = '#b12a1c';
    const lsmColor = '#c65431';
    const maxSpecs = 6;
    const descriptionDefaultCharCount = 20;
    const descriptionExpandedCharCount = 150;
    const [timeRemaining, setTimeRemaining] = useState(props.timeRemaining);
    const [isTimerActive, setIsTimerActive] = useState(true);
    const [showExpandedDescription, setShowExpandedDescription] = useState(false);
    const [descriptionCharCount, setDescriptionCharCount] = useState(descriptionDefaultCharCount);
    //use useMemo
    const noPhone = useMemo(() => {
        const hasExclusiveWholesalePhone = props.ShowWholeSale && props.IsExclusiveWholeSale &&
            props.WholeSalePhone && props.WholeSalePhoneUnformatted && !props.RetailPhoneNumber;

        const hasRetailPhone = props.RetailPhoneNumber && props.ShowDealerPhoneNumber &&
            !props.ShowWholesaleContactInfoOnly;

        const hasWholesalePhone = ((props.ShowWholeSale || props.PreAuctionTimeDate) &&
            props.ShowDealerPhoneNumber && !props.IsExclusiveWholeSale) &&
            !props.IsRY && !props.RetailPhoneNumber;

        const hasRentalPhone = props.RentalContactNumber &&
            (!props.RetailPhoneNumber || props.IsRY);

        return !hasExclusiveWholesalePhone && !hasRetailPhone &&
            (!props.IsForRentOnly && !props.IsRY) && !hasWholesalePhone && !hasRentalPhone;
    }, [props.ShowWholeSale, props.IsExclusiveWholeSale, props.RetailPhoneNumber, props.ShowDealerPhoneNumber, props.PreAuctionTimeDate, props.ShowWholesaleContactInfoOnly]);

    let secondsLeftToAddListingToCountdown = maxSecondsAuctionTimeListingCanBeExtendedDueToBidding + 5; //5 seconds to account for the timer's first tick

    useEffect(() => {
        ready(() => {
            if (!(props.Id && props.Id > 0 || props.DealerGroupID && props.DealerGroupID > 0)) {
                logSAMError(routes.LogSendAMessageError, 'Listing has no ID or no DealerGroup ' + window.location.href);
            }
            if (!windowSize.isMobile) {
                adjustElementHeightsByRow('list-listing-title');
                adjustElementHeightsByRow('below-price-container');
            }
        });
    }, []);

    useEffect(() => {
        adjustElementHeightsByRow('list-listing-title');
        adjustElementHeightsByRow('below-price-container');
    }, [windowSize.width]);

    useEffect(() => {
        if (!windowSize.isMobile && props.AuctionBiddingData?.IsAuctionLive) {
            adjustElementHeightsByRow('list-listing-title');
            adjustElementHeightsByRow('below-price-container');
        }
    }, [props.AuctionBiddingData])

    useEffect(() => {
        handleSpecsOrDescriptionChange(props.showAllSpecs, props.expandDescription);
    }, [props.showAllSpecs, props.expandDescription]);

    const handleSpecsOrDescriptionChange = (showSpecs, showDescription) => {
        setShowExpandedSpecs(showSpecs);

        if (showDescription) {
            setDescriptionCharCount(descriptionExpandedCharCount);
        } else {
            setDescriptionCharCount(descriptionDefaultCharCount);
        }
        setShowExpandedDescription(showDescription);
    }

    const adjustElementHeightsByRow = useCallback((className) => {
        const elements = document.querySelectorAll(`.${className}`);
        if (!elements.length) return;

        const itemsPerRow = windowSize.width > 1251 ? 4 : windowSize.width > 1000 ? 2 : 1;
        const rows = [];
        let currentRow = [];

        const elementData = Array.from(elements).map((element, index) => {
            element.style.height = "";

            currentRow.push(element);

            if ((index + 1) % itemsPerRow === 0 || index === elements.length - 1) {
                rows.push([...currentRow]);
                currentRow = [];
            }

            return {
                element,
                height: element.getBoundingClientRect().height // More accurate than clientHeight
            };
        });

        requestAnimationFrame(() => {
            let elementIndex = 0;

            rows.forEach(row => {
                const rowElements = elementData.slice(elementIndex, elementIndex + row.length);
                const maxHeight = Math.max(...rowElements.map(data => data.height));

                rowElements.forEach(data => {
                    data.element.style.height = `${maxHeight}px`;
                });

                elementIndex += row.length;
            });
        });
    }, [windowSize.width]);

    const openEmailModal = (inquiry) => {
        let contact = contactType.Retail;
        if (props.IsExclusiveWholeSale) {
            contact = contactType.ExclusiveWholesale;
        }

        props.openEmailModal(inquiry, contact, {
            ModalTitle: props.ListingTitle,
            Subject: props.ListingTitle,
            DealerCRMID: props.DealerCRMID,
            DealerGroupID: props.DealerGroupID,
            BranchName: props.Dealer,
            DealerLocation: props?.DealerLocation || props?.ListingLocation?.ShortLocation || props?.ListingLocation?.FormattedLocation,
            DealerPhone: props.RetailPhoneNumber || '',
            DealerContact: props.DealerContact || '',
            ListingManufacturer: props.ManufacturerName,
            ListingModel: props.Model,
            ListingAltCategoryID: props.AltCategoryId,
            ListingBaseCatID: props.BaseCategoryId,
            ListingCategoryID: props.CategoryId,
            ListingID: props.Id,
            ListingSourceDomainUrl: props.ListingSourceDomainUrl,
            SerialNumber: props.SerialNumber,
            Price: props.RetailPrice,
            WholeSalePrice: props.WholeSalePrice,
            ListingURL: props.SAMUrl ?? props.DetailUrl,
            IsParts: false,
            IsForRentOnly: props.IsForRentOnly,
            IsExclusiveWholeSale: props.IsExclusiveWholeSale,
            ListingCondition: props.Condition,
            Ref: ref.current,
            IsPreAT: props.PricingInfo?.PreAuctionTimeDate && props.IsFTISearch,
            IsWholeSale: props.IsFTISearch,
            IsPreTrade: props.IsPreTrade,
            HasFormAssembly: props.HasFormAssembly,
        });
    };

    const toggleEmployeeModal = useCallback(() => {
        if (!showEmployeeModal) {
            setScrollPosition();
        } else {
            unsetScrollPosition();
        }
        setShowEmployeeModal(showEmployeeModal => !showEmployeeModal);
    }, [showEmployeeModal]);

    const phoneClickToTrack = (e, phoneNumber, phoneType) => {
        if (!props.IsSearchEngine) {
            let element = e.target;
            sandhillsRequest({
                url: props.PhoneClickToTrackURL,
                method: 'post',
                data: {
                    PhoneNumber: phoneNumber,
                    PhoneType: phoneType
                },
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then(() => {
                element.setAttribute('data-impression-logged', 'true');
            }).catch(() => {
                element.setAttribute('data-impression-logged', 'false');
            });
        }
    };

    const addListing = (list, listing) => {
        const exists = list.some(item => item.Id === listing.Id);
        if (!exists) {
            return [...list, listing];
        }
        return list;
    };

    const removeListing = (list, listingId) => {
        return list.filter(item => item.Id !== listingId);
    };

    useEffect(() => {
        if (currentSite.IsAuctionTimeAuctionSite && props.IsAT) {
            if (timeRemaining <= secondsLeftToAddListingToCountdown && !(props.timeRemaining <= -10) && isTimerActive) {
                props.setCountdownListings(prevList => addListing(prevList, props));

            } else if (timeRemaining < -10 && props.timeRemaining < -10) {
                props.setCountdownListings(prevList => removeListing(prevList, props.Id));
                setIsTimerActive(false)
            } else {
                props.setCountdownListings(prevList => {
                    if (prevList && prevList.some(item => item.Id === props.Id)) {
                        return removeListing(prevList, props.Id);
                    }
                    return prevList || [];
                });
            }
        }
    }, [props.timeRemaining, timeRemaining]);

    const handleTick = (timeRemainingInMilliseconds) => {
        let timerRemainingFromCountDown = Math.floor(timeRemainingInMilliseconds / 1000);

        setTimeRemaining(timerRemainingFromCountDown);
    };

    const handleDescriptionToggle = () => {
        if (!showExpandedDescription) {
            setDescriptionCharCount(descriptionExpandedCharCount);
        };
        setShowExpandedDescription(!showExpandedDescription);
    }

    //Countdown renderer for AT listings
    const countDownRenderer = ({ days, hours, minutes, seconds, completed }) => {
        if (completed || seconds < 0) {
            return <TranslationText tKey='sandhills.tradepubwebsite.mvc.auctionhasended' />;
        } else {
            if (seconds > 0 && minutes > 5) minutes += 1;

            const isUnderOneHour = days === 0 && hours === 0 && minutes <= 60;
            const formatNumber = (number, isRed) => (
                <strong className={isRed ? 'red-text' : ''}>{number}</strong>
            );

            return (
                <span>
                    {days > 0 ? (
                        <>
                            {formatNumber(days, false)}{' '}
                            <span>{days === 1 ? translator.translate('sandhills.day') : translator.translate('sandhills.days')}</span>{' '}
                            {hours > 0 && (
                                <>
                                    {formatNumber(hours, false)}{' '}
                                    <span>{hours === 1 ? translator.translate('sandhills.hour') : translator.translate('sandhills.hours')}</span>
                                </>
                            )}
                        </>
                    ) : hours > 0 ? (
                        <>
                            {formatNumber(hours, isUnderOneHour)}{' '}
                            <span>{hours === 1 ? translator.translate('sandhills.hour') : translator.translate('sandhills.hours')}</span>{' '}
                            {minutes > 0 && (
                                <>
                                    {formatNumber(minutes, isUnderOneHour)}{' '}
                                    <span>min</span>
                                </>
                            )}
                        </>
                    ) : minutes > 0 ? (
                        <>
                            {formatNumber(minutes, isUnderOneHour)}{' '}
                            <span>min</span>{' '}
                            {minutes < 7 && hours === 0 && (
                                <>
                                    {formatNumber(seconds, isUnderOneHour)}{' '}
                                    <span>{seconds === 1 ? translator.translate('sandhills.second') : translator.translate('sandhills.seconds')}</span>
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            {formatNumber(seconds, isUnderOneHour)}{' '}
                            <span>{seconds === 1 ? translator.translate('sandhills.second') : translator.translate('sandhills.seconds')}</span>
                        </>
                    )}
                </span>
            );
        }
    };

    const saveIterableAction = (actionName) => {
        sandhillsRequest({
            url: routes.SaveAction,
            method: 'post',
            data: {
                'email': '',
                'actionName': actionName,
                'manufacturers': [props.ManufacturerName],
                'models': [props.Model],
                'listingTypes': props.ListingTypes,
                'categoryIDs': getDistinct([props.CategoryId, props.AltCategoryId]),
                'listingIDs': [props.Id],
                'conditions': [props.Condition]
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        });
    };

    const handleBuyNowClick = () => {
        gtmPushEvent(gtmEventType.buyNowClick);
        saveIterableAction('buyNow');
    }

    const handleTelClick = (e) => {
        if (e == phoneType.Rental) gtmPushEvent(gtmEventType.callRental);
        else if (e == phoneType.Wholesale) gtmPushEvent(gtmEventType.callWholesale);
        else gtmPushEvent(gtmEventType.call);
        saveIterableAction('telClick');
    }

    const getBuyersPremiumData = (listingID, currencyCode) => {
        sandhillsRequest({
            url: routes.GetBuyersPremiumData,
            data: {
                listingID: listingID,
                currencyCode: currencyCode
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((response) => {
            props.SetBuyersPremiumData(response)
        });
    };

    const showPreBidBox = () => {
        return props?.AuctionBiddingData?.BiddingAllowed && props.IsEF;
    };

    const toggleWatchlistExpansion = (count) => {
        setUnsaveWatchlist(false);
        if (!displayWatchlistExpansion && !savedToWatchlist) {
            props.addToWatchlistCount();
        }
        if (displayWatchlistExpansion) {
            props.getUserWatchlist();
        }
        if (count == 0) {
            setUserWatchlist(userWatchlist.filter((c) => props.IsWatchList ? c.Id != props.Id : c.ListingID != props.Id));
            setUnsaveWatchlist(true);
        }
        setDisplayWatchlistExpansion(!displayWatchlistExpansion);
    }

    const getWholesaleBrandingAndWholesaleRetailPrice = (containerClass) => {
        if (props.IsFTISearch && props.RetailPrice) {
            return (
                <div className={containerClass}>
                    <span className='branding-group'>
                        {props.IsPreTrade ?
                            <span className='wholesale-text pre-trade-text'>{translator.translate('sandhills.tradesites.mvc.pretradeoffer')}</span>
                            :
                            props.IsPrivateParty ?
                                <span className='wholesale-text private-party-text'>{translator.translate('sandhills.tradesites.mvc.privateparty')}</span>
                                :   
                                <span className='wholesale-text'>{translator.translate('sandhills.crm.dscrm.wholesaleprice')}</span>
                        }
                        {props.PricingInfo?.PreAuctionTimeDate ?
                            <img src={routes.CDNBaseUrl + 'Images/Logos/FastTrack/pre-at-logo.png'} aria-hidden={true} /> :
                            <img src={routes.CDNBaseUrl + 'Images/Logos/FastTrack/fti-logo.png'} aria-hidden={true} />}
                    </span>
                    {!props.IsPreTrade &&
                        <>  
                            {!props.IsPrivateParty && 
                                <span className='msrp-group'>
                                    <span className='spec-label'>
                                        <TranslationText tKey='sandhills.tradepubwebsite.mvc.retail' />:&nbsp;
                                    </span>
                                    <span className='msrp'>{props.RetailPrice}</span>&nbsp;&nbsp;
                                </span>    
                            }
                            <span className='price'>{props.WholeSalePrice}</span>
                        </>}
                    <br />
                </div>
            );
        }
    }

    const renderMSRPSection = () => {
        const registrationLink = currentSite.RequiresFullRegistration && currentUser.IsMinReg && currentUser?.IsAuthenticated
            ? <a href={routes.FullRegistrationRoute} style={{ fontSize: '14px' }} rel={LinkRelationTypes.noFollow}><TranslationText tKey='sandhills.tradesitesmvc.completelogintoseemore' /></a>
            : <a href={props.LoginLink} style={{ fontSize: '14px' }} rel={LinkRelationTypes.noFollow}><TranslationText tKey='sandhills.logintoseemoredetails' /></a>;

        const msrpPriceContent = !UnauthorizedAuctionResultRequest && !currentSite.RequiresFullRegistration || (currentSite.RequiresFullRegistration && !currentUser.IsMinReg)
            ? <span className='price'>{props.RetailPrice}</span>
            : registrationLink;

        return (
            <>
                {props.ShowMSRPLabel ? (
                    <>
                        {getWholesaleBrandingAndWholesaleRetailPrice('price-wrapper wholesale-group')}
                        <span className='msrp-group'>
                            <span className='spec-label'>
                                <TranslationText tKey='sandhills.msrp' />
                            </span>
                            <span className='msrp'>{props.MSRP}</span>&nbsp;&nbsp;
                        </span>
                        {props.ShowMSRPSaveLabel && props.MSRPSavePercentage && (
                            <span className='msrp-save-percentage'> {props.MSRPSavePercentage} </span>
                        )}
                        {msrpPriceContent}
                    </>
                ) : (
                    <div className='MSRP-parent'>
                        <span className='spec-label' dangerouslySetInnerHTML={{ __html: props.PriceLabel + ':' }}></span>
                        <span className='msrp'>{props.MSRP}</span>
                        {props.ShowMSRPSaveLabel && props.MSRPSavePercentage && (
                            <span className='msrp-save-percentage'> {props.MSRPSavePercentage} </span>
                        )}
                        {msrpPriceContent}
                    </div>
                )}
            </>
        );
    };

    const GetPriceLabelAndValue = () => {
        return (
            <div className='price-contain'>
                {(props.IsAT || props.IsEF) && !props.IsAuctionResult &&
                    <>
                        {props.AuctionBiddingData?.CurrentBidFormatted ?
                            <>
                                {(props.AuctionBiddingData.BiddingAllowed && props.IsEF) &&
                                    <>
                                        <span className='spec-label'>{props.AuctionBiddingData.BidLabel}:</span>
                                        <span className="auction-price">{props.AuctionBiddingData.CurrentBidFormatted}</span>
                                        {(!currentSite?.Site?.IsAuctionTime && props.IsEF && currentUser?.IsAuthenticated && currentUser?.BidderStatus == bidderStatus.ACTIVE) && !props.AuctionBiddingData.BiddingAllowed &&
                                            <p className='pre-bid-not-available'>{translator.translate('sandhills.prebiddingnotavailable')}</p>
                                        }
                                        <br />
                                        <span className='quantity-label'>{props.AuctionBiddingData.CalculatedHeadCountPrice > 0 ? props.QuantityLabel + props.AuctionBiddingData.CalculatedHeadCountPrice : props.AuctionBiddingData.CurrentBid > 0 ? props.QuantityLabel : props.QuantityLabel ? props.QuantityLabel : ""}</span>
                                    </>
                                }
                            </>
                            :
                            <div>&nbsp;</div>
                        }
                    </>}
                {props.ShowWholeSale && props.IsExclusiveWholeSale && props.WholeSalePrice && !props.IsRY && !props.IsFTISearch && !props.IsPrivateParty &&
                    <div className='site-data-section'>
                        <span className='exclusive-wholesale-price-label'>
                            {!props.ShowWholesalePriceOnly && <span className='spec-label'><TranslationText tKey='Sandhills.CRM.DSCRM.Wholesale' />:</span>}
                            <span className='exclusive-wholesale-price' ><strong>{props.WholeSalePrice}</strong></span>
                        </span>
                    </div>}
                {!props.IsDismantled &&
                    <>
                        {props.FormattedDiscountPrice && !props.IsRY && !props.IsPreTrade ?
                            <div className='MSRP-parent'>
                                <span className='spec-label' dangerouslySetInnerHTML={{ __html: props.PriceLabel + ':' }}></span>
                                <span className='msrp'>{props.RetailPrice}</span>
                                {props.DiscountSavePercentage && <span className='msrp-save-percentage'> {props.DiscountSavePercentage} </span>}
                                <div className='price'>{props.FormattedDiscountPrice}</div>
                            </div>
                            :
                            <div>
                                {props.MSRP && props.ShowMSRPField && !props.ShowWholesalePriceOnly && !props.IsRY && !props.IsPreTrade ?
                                    <>{renderMSRPSection()}</>
                                    :
                                    (props.RetailPrice || props.AlternateRetailPrice || UnauthorizedAuctionResultRequest) && !props.ShowWholesalePriceOnly && !props.IsRY && !(props.AuctionBiddingData?.BiddingAllowed && props.IsEF) &&
                                    <div className='retail-price-container'>
                                        {(props.IsAT || props.IsEF) && !props.IsAuctionResult && <>
                                            {props.AuctionBiddingData?.CurrentBidFormatted ?
                                                <>
                                                    {(props.AuctionBiddingData.BiddingAllowed && props.IsEF) ?
                                                        <div>&nbsp;</div>
                                                        :
                                                        <div>
                                                            <strong>
                                                                <span className='spec-label'>{props.AuctionBiddingData.BidLabel}:</span>
                                                                <span className="auction-price">{props.AuctionBiddingData.CurrentBidFormatted}</span>
                                                                {(!currentSite?.Site?.IsAuctionTime && props.IsEF && currentUser?.IsAuthenticated && currentUser?.BidderStatus == bidderStatus.ACTIVE) &&
                                                                    <p className='pre-bid-not-available'>{translator.translate('sandhills.prebiddingnotavailable')}</p>
                                                                }
                                                                <br />
                                                                <span className='quantity-label'>{props.AuctionBiddingData.CalculatedHeadCountPrice > 0 ? props.QuantityLabel + props.AuctionBiddingData.CalculatedHeadCountPrice : props.AuctionBiddingData.CurrentBid > 0 ? ' ' + props.QuantityLabel : props.QuantityLabel ? props.QuantityLabel : ""}</span>
                                                            </strong>
                                                        </div>
                                                    }
                                                </>
                                                :
                                                <div>&nbsp;</div>
                                            }
                                        </>}
                                        {((!props.IsAT && !props.IsEF) || props.IsAuctionResult) && (props.RetailPrice || UnauthorizedAuctionResultRequest) && !props.IsFTISearch &&
                                            <>
                                                {UnauthorizedAuctionResultRequest && !currentSite.RequiresFullRegistration || (currentSite.RequiresFullRegistration && currentUser.IsMinReg) ?
                                                    {
                                                        ...currentSite.RequiresFullRegistration && currentUser.IsMinReg && currentUser?.IsAuthenticated ?
                                                            <a href={routes.FullRegistrationRoute} style={{ fontSize: '14px' }} rel={LinkRelationTypes.noFollow}><TranslationText tKey='sandhills.tradesitesmvc.completelogintoseemore' /></a>
                                                            :
                                                            <a href={props.LoginLink} style={{ fontSize: '14px' }} rel={LinkRelationTypes.noFollow}><TranslationText tKey='sandhills.logintoseemoredetails' /></a>
                                                    }
                                                    :
                                                    <span className='price'>{props.PriceTaxModel && props.PriceTaxModel.DisplayPriceInclusiveTax && props.PriceTaxModel.RetailPriceWithTax ? props.PriceTaxModel.RetailPriceWithTax : props.RetailPrice}</span>
                                                }
                                            </>}
                                        {props.AlternateRetailPrice && !UnauthorizedAuctionResultRequest && !props.IsRY && !props.IsCH && !props.IsFTISearch &&
                                            <div className='retail-price-container'>
                                                {props.AlternatePriceLabel && <span className='spec-label'>{props.AlternatePriceLabel}:</span>}
                                                <span className='price'>{props.AlternateRetailPrice}</span>
                                            </div>}
                                        {getWholesaleBrandingAndWholesaleRetailPrice('price-wrapper wholesale-group')}
                                    </div>}
                            </div>}
                    </>}
            </div>
        );
    }

    return (
        <div id={props.Id} className={props.IsFeaturedListing ? 'ListingParentElement ' + (props.UseFeaturedListingBorder ? "feat-listing-border" : "") : ((props.IsPremiumListing && !props.IsFeaturedListing) ? ' list-premium-listing' : 'ListingParentElement')} {...(props.UseDataProperties && { 'data-currency': props.CurrencyCode, 'data-item-brand': props.ManufacturerName, 'data-item-category': props.DisplayCategoryName, 'data-item-name': props.ListingTitle, 'data-price': props?.Price > 0 ? props.Price : props?.AuctionBiddingData?.CurrentBid > 0 ? props.AuctionBiddingData.CurrentBid : null, 'data-lid': props.ListingID, 'data-offers': `${props.ShippingEstimateData && props.ShippingEstimateData.EstimateDisplay ? 'shipping,' : ''} ${props.Widgets.ShowMonthlyFinanceRatesOnListings && props.Widgets.ShowLenderWidget ? 'financing' : ''}` })}>
            {showEmployeeModal &&
                <EmployeeListingModal
                    {...props.EmployeeListingViewModel}
                    listingTitle={props.ListingTitle}
                    inventoryManagementUrl={props.InventoryManagementUrl}
                    toggleEmployeeModal={toggleEmployeeModal}
                    sortApplied={props.sortApplied}
                    listingId={props.Id} />
            }
            <div className='listing-card-grid listing-data-selector' data-listing-id={props.Id}>
                {((props.IsEF || props.IsAT) && props?.AuctionBiddingData?.UserBidInfo?.ListingBidStatusText) && !props.IsSimilar &&
                    <div className={'bidder-status' + (props.AuctionBiddingData.UserBidInfo.UserIsOutBid ? ' bidder-status_outbid' : ' bidder-status_high-bidder')}>
                        <h3>{props.AuctionBiddingData.UserBidInfo.ListingBidStatusText}</h3>
                    </div>
                }
                <div className='extra-space'>
                    <div className='list-listing-title'>
                        {(props.LotNumber || (props.ShowAuctionBranding && (props.IsAT || props.IsEF))) &&
                            <div className='lot-number'>
                                {props.LotNumber && <span>{translator.translate('sandhills.auctiontime.api.lotnumber')} {props.LotNumber}</span>}
                                {(props.AuctionBiddingData?.IsAuctionLive || (props.ShowAuctionBranding && (props.IsAT || props.IsEF))) &&
                                    <div className='live-tile' style={{ marginLeft: props.LotNumber ? '5px' : '', backgroundColor: ((currentSite?.Site?.IsEquineMarket || currentSite?.Site?.IsLivestockMarket) && props.IsEF) ? lsmColor : props.IsAT ? atColor : props.IsEF ? efColor : '' }}>
                                        {props.IsAT ?
                                            <img src={routes.CDNBaseUrl + 'Images/Icons/Miscellaneous/at-icon-white.png'} alt="Auction Time Logo" style={{ marginRight: props.AuctionBiddingData?.IsAuctionLive ? '5px' : '' }}></img>
                                            : currentSite?.Site?.IsLivestockMarket || currentSite?.Site?.IsEquineMarket ?
                                                <img src={routes.CDNBaseUrl + 'Images/Icons/Miscellaneous/production-sale.png'} alt="Production Sale Logo" style={{ marginRight: ((props.AuctionBiddingData?.IsAuctionLive && props.IsAT) || (!props.IsAuctionResult && props?.AuctionBoardURL && props.IsEF)) ? '5px' : '' }}></img>
                                                :
                                                <img src={routes.CDNBaseUrl + 'Images/Icons/Miscellaneous/ef-icon-black.png'} alt="Equipment Facts Logo" style={{ marginRight: (!props.IsAuctionResult && props?.AuctionBoardURL && props.IsEF) ? '5px' : '' }}></img>
                                        }
                                        {((props.AuctionBiddingData?.IsAuctionLive && props.IsAT) || (!props.IsAuctionResult && props?.AuctionBoardURL && props.IsEF)) && <span style={{ color: props.IsAT || (currentSite?.Site?.IsLivestockMarket || currentSite?.Site?.IsEquineMarket) ? '#ffffff' : '#000000' }}>{translator.translate('sandhills.tradepubwebsite.mvc.live')}</span>}
                                    </div>}
                            </div>
                        }
                        {props.IsPreATResult && currentSite?.Site?.IsHostedSite &&
                            <div className='lot-number'>
                                <img className='pre-at-result' src={routes.CDNBaseUrl + 'Images/Icons/AuctionTime/PreAuctionTimeIcon.png'} alt="" />
                            </div>
                        }
                        <div className='listing-title'>
                            {props.ShowEmployeeOnlyInfo &&
                                <>
                                    <a className='open-spec-link' aria-label={translator.translate('sandhills.tradesites.mvc.viewspecs')} role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); toggleEmployeeModal(); } }} onClick={toggleEmployeeModal}>
                                        <i className='fas fa-external-link-alt'></i>
                                    </a>&nbsp;
                                </>}
                            {props.DetailUrl && props.ListingTitle ? (
                                <h2 className="listing-portion-title">
                                    {props.openDetailPageInNewTab ?
                                        <a href={props.DetailUrl} className='list-listing-title-link' target={linkTarget} aria-label={props.ListingTitle + " " + props.DisplayCategoryName + " (" + translator.translate('sandhills.opensinanewtab') + ")"}>
                                            <strong>{props.ListingTitle}</strong>
                                            {" "}
                                            <p className='listing-category' dangerouslySetInnerHTML={{ __html: props.DisplayCategoryName }} />
                                        </a>
                                        :
                                        <>
                                            <a href={props.DetailUrl} className='list-listing-title-link' target={linkTarget}>
                                                <strong>{props.ListingTitle}</strong>
                                            </a>
                                            {" "}
                                            <p className='listing-category' dangerouslySetInnerHTML={{ __html: props.DisplayCategoryName }} />
                                        </>
                                    }
                                </h2>
                            ) : (
                                <h2 className="listing-portion-title">
                                    {props.ListingTitle}
                                    {" "}
                                    <span className='listing-category' dangerouslySetInnerHTML={{ __html: props.DisplayCategoryName }} />
                                </h2>
                            )}
                            {props.IsWatchList && !props.IsRecommendedListingsSearch &&
                                <a className='listings-watchlist-lnk watchlist-delete' aria-label={translator.translate('sandhills.removefromwatchlistwithspace')} role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); props.WatchlistClickDelete(props.Id); } }} onClick={() => props.WatchlistClickDelete(props.Id)} data-listing-id={props.Id} data-action='delete'>
                                    <i className='fas fa-times'></i>
                                </a>}
                        </div>
                    </div>
                    <div className='options-popper-container'>
                        <OptionsPopper ShowCompare={props.ShowCompare} UpdateCompareListing={() => props.UpdateCompareListing(props.Id, props.ListingImageModel.DisplayImageMediaId, !props.IsCheckedForCompare, props.ListingImageModel.ImageUrl[0])}
                            IsCheckedForCompare={props.IsCheckedForCompare}
                            IsDisabled={props.IsCompareChkBoxDisabled}
                            CompareUrl={props.CompareUrl}
                            Id={props.Id}
                            classNames={'compare-listings'}
                            ownerCRMID={props.OwnerCRMID}
                            externalID={props.ExternalID}
                            ShowCompareURL={props.ShowCompareURL}
                            baseCatID={props.BaseCategoryId}
                            />
                    </div>
                    <ListingGridImage ListingId={props.Id} ListingTitle={props.ListingTitle} AbsolutePathBaseUrl={props.AbsolutePathBaseURL} IsAT={props.IsAT} IsEF={props.IsEF} IsAuctionResult={props.IsAuctionResult} DetailUrl={props.DetailUrl} LinkTarget={linkTarget}
                        LazyLoad={props.LazyLoad} {...props.ListingImageModel} LazyLoad={props.LazyLoad} ShowAuctionBranding={props.ShowAuctionBranding}
                        ForceLoadImages={props.ForceLoadImages} DispatchListings={props.DispatchListings} BaseCategoryId={props.BaseCategoryId}
                        IsPreAuction={props.PricingInfo?.PreAuctionTimeDate} IsAuctionLive={props.AuctionBiddingData?.IsAuctionLive}
                        UserWatchlist={props.UserWatchlist} toggleWatchlistExpansion={toggleWatchlistExpansion} unsaveWatchlist={unsaveWatchlist}
                        IsFeaturedListing={props.IsFeaturedListing} IsPremiumListing={props.IsPremiumListing} IsFeaturedAuction={props.ListingImageModel?.IsFeaturedAuction}
                        CategoryID={props.CategoryId} ShowWatchlist={props.ShowWatchList} setSavedToWatchlist={setSavedToWatchlist}
                        ShowMediaOverlayBanner={props.ShowMediaOverlayBanner} MediaOverlayBanner={props.MediaOverlayBanner} IsPreTrade={props.IsPreTrade} IsPreATResult={props.IsPreATResult}
                    >
                    </ListingGridImage>
                    {displayWatchlistExpansion &&
                        <WatchlistExpansion {...props} Id={props.Id} toggleWatchlistExpansion={toggleWatchlistExpansion} CustomWatchlists={props.CustomWatchlists} />
                    }

                    {GetPriceLabelAndValue()}
                    <div className='below-price-container'>
                        {props.Widgets.ShowMonthlyFinanceRatesOnListings && props.Widgets.ShowLenderWidget && props.Widgets.PaymentsAsLowAs != '' &&
                            <PaymentsAsLowAsLink
                                {...props.Widgets}
                                Manufacturer={props.ManufacturerName}
                                Model={props.Model}
                                ListingType={props.ListingType}
                                ListingID={props.Id}
                                Condition={props.Condition}
                                CategoryId={props.CategoryId}
                                AltCategoryId={props.AltCategoryId}
                                IsRedesign
                            />}

                        {props.SNGuideRoute &&
                            <div className='sn-guide-link'>
                                <a href={props.SNGuideRoute} className='sn-route' target='_blank'>
                                    {translator.translate('sandhills.tradesites.mvc.viewhistoricalrecords')}<i className='fas fa-external-link-alt' />
                                </a>
                            </div>
                        }
                    </div>
                    {props.IsPreTrade &&
                        <div className='mao-button' ref={ref} onClick={() => openEmailModal(inquiryType.Offer)}>
                            {translator.translate('sandhills.makeanoffer')}
                        </div>
                    }

                    <div className='widget-container'>
                        {props.Widgets && !props.Widgets.HideAllWidgets && !props.IsSimilar && <WidgetList {...props.Widgets} Manufacturer={props.ManufacturerName} Model={props.Model} ListingType={props.ListingType} ListingID={props.Id} Condition={props.Condition} CategoryId={props.CategoryId} AltCategoryId={props.AltCategoryId} IsRedesign />}
                        {isLivestockHayStaw &&
                        {
                            ...priceDesc && priceDesc.length > 0 ?
                                <>
                                    <span className='spec-label'>{priceDesc[0].Key}:&nbsp;</span>
                                    <span className='spec-value'>{truncateString(priceDesc[0].Value, 80)}</span>
                                </>
                                :
                                <>
                                    {
                                        props.Specs && props.Specs.length > 0 && !(props.IsAT || props.IsEF) &&
                                        <>
                                            <span className='spec-label'>{props.Specs[0].Key}:&nbsp;</span>
                                            <span className='spec-value'>{truncateString(props.Specs[0].Value, 80)}</span>
                                        </>
                                    }
                                </>
                        }
                        }
                    </div>

                    <ListingDetailLink IsAT={props.IsAT} IsEF={props.IsEF} IsPremiumListing={props.IsPremiumListing}
                        isAuctionOnToday={props.isAuctionOnToday} IsAuctionResult={props.IsAuctionResult}
                        openDetailPageInNewTab={props.openDetailPageInNewTab} DetailUrl={props.DetailUrl} NextBidAmount={props.AuctionBiddingData?.NextBidAmount}
                        IsAuctionLive={props.AuctionBiddingData?.IsAuctionLive} AddMarginTop={!props.Widgets.ShowLenderWidget && !props.Widgets.ShowEscrowWidget && !props.Widgets.ShowInsuranceWidget && !props.Widgets.ShowCostCalculatorWidget && !props.Widgets.ShowHaulerWidget} />

                    {!props.IsAuctionResult && !props.IsSimilar && props?.AuctionBoardURL &&
                        < div className='enter-auction-btn'>
                            <a className='ts-button red-btn' target='_blank' href={props.AuctionBoardURL} aria-label={translator.translate('sandhills.tradepubwebsite.mvc.enterliveauction') + " (" + translator.translate('sandhills.opensinanewtab') + ")"}>
                                {translator.translate('sandhills.tradepubwebsite.mvc.enterliveauction')}</a>
                        </div>
                    }
                    {(props.FleetEvaluatorModel) &&
                        <FleetEvaluatorValues FEAuctionValue={props.FleetEvaluatorModel.FEAuctionValue} FERetailValue={props.FleetEvaluatorModel.FERetailValue} FEImagePath={props.FleetEvaluatorModel.FEImagePath} />
                    }

                    {(props.AuctionBiddingData?.CurrentBidFormatted && showPreBidBox()) &&
                        <div className='listing-bidding-box'>
                            <ListingBiddingBox
                                AuctionBiddingData={props.AuctionBiddingData}
                                resetAuctionData={props.resetAuctionData}
                                IsBuyersPremium={props.IsBuyersPremium}
                                getBuyersPremiumData={getBuyersPremiumData}
                                dealerName={props.Dealer}
                                notificationSettings={props.notificationSettings}
                                WatchlistUrl={props.WatchlistURL}
                                IsAT={props.IsAT}
                                IsEF={props.IsEF}
                                SellerCRMID={props.AccountCRMID}
                                showBoxOnly
                                IsRedesign
                                hideCurrentBid />
                        </div>
                    }
                    {props.BuyNowURL && !props.ShowWholesalePriceOnly &&
                        <a className='buy-btn gtm-buynow des-buy-now' target={(props.BuyNowNewTab || currentSite.IsMobileAppContext) ? '_blank' : '_self'} href={props.HasVariants ? props.Variants?.length === 1 ? `${props.BuyNowURL}&variantId=${props.Variants[0].VariantID}` : props.DetailUrl : props.BuyNowURL} onClick={handleBuyNowClick} aria-label={(props.BuyNowNewTab || currentSite.IsMobileAppContext) ? (props.HasVariants && props.Variants?.length > 1 ? translator.translate('sandhills.selectoptions') : props.BuyNowButtonText) + " (" + translator.translate('sandhills.opensinanewtab') + ")" : props.HasVariants && props.Variants?.length > 1 ? translator.translate('sandhills.selectoptions') : props.BuyNowButtonText}>
                            <i className={props.IsRY ? 'fa-solid fa-key gtm-noclick' : 'fas fa-shopping-cart gtm-noclick'} aria-hidden='true' />
                            <span>{props.HasVariants && props.Variants?.length > 1 ? <TranslationText tKey='sandhills.selectoptions' /> : props.BuyNowButtonText}</span>
                        </a>}
                    {props.PriceTaxModel && !props.IsRY &&
                        <span className='additional-price-info-grid'>
                            {props.PriceTaxModel.RetailPriceWithTax &&
                                    <span className='spec-label price-tax'>{props.RetailPrice} + {props.PriceTaxModel.TaxRateType} = {props.PriceTaxModel.RetailPriceWithTax}<br />
                                        ({props.PriceTaxModel.TaxAppliesInTranslatedText})</span>}
                            {props.PriceTaxModel.RetailPriceWithoutTax &&
                                <span className='spec-label price-tax'>{props.PriceTaxModel.RetailPriceWithoutTax} + {props.PriceTaxModel.TaxRateType} = {props.RetailPrice}<br />
                                        ({props.PriceTaxModel.TaxAppliesInTranslatedText})</span>}
                            {props.PriceTaxModel.TaxNotApplicable &&
                                <span className='spec-label price-tax'>{props.PriceTaxModel.TaxAppliesInTranslatedText}</span>}
                        </span>}
                    <div className='listing-content'>
                        {!props.IsAuctionResult && !props.IsSimilar &&
                            <>
                                {props.IsAT &&
                                    <>
                                        {props.IsBuyersPremium != null ?
                                            (props.IsBuyersPremium == true ?
                                                <span className='spec-value bold red-text buyers-premium-link' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); getBuyersPremiumData(props.Id, props.CurrencyCode); } }} onClick={() => getBuyersPremiumData(props.Id, props.CurrencyCode)}><TranslationText tKey='Sandhills.ViewBuyersPremium' /></span> :
                                                <span className='spec-value'><TranslationText tKey='Sandhills.NoBuyersPremium' /></span>) : null}
                                        {props.AuctionBiddingData?.NumberOfBids > 0 &&
                                            <div>
                                                <span className='spec-label'><TranslationText tKey='Sandhills.TradePubWebSite.MVC.NumberOfBids' />: </span>
                                                <span className='spec-value'>{props.AuctionBiddingData?.NumberOfBids}</span>
                                            </div>}
                                        {props.AuctionBiddingData?.LiveBiddingOpens &&
                                            <div dangerouslySetInnerHTML={{ __html: props.AuctionBiddingData?.LiveBiddingOpens }} />}
                                        {(props.AuctionBiddingData?.TimeLeft || props.AuctionBiddingData?.TimeRemaining / 1000 > -10) && !props.AuctionBiddingData?.IsAllBiddingPaused && !props.AuctionBiddingData?.IsListingPaused ?
                                            <div>
                                                <span className='spec-label opens'><TranslationText tKey='Sandhills.TimeRemaining' />:</span>
                                                {currentSite.IsAuctionTimeAuctionSite && isTimerActive ?
                                                    <>
                                                        <span className='spec-value'><Countdown date={props.endTime || props.AuctionBiddingData?.ProjectedEndDateTime} renderer={countDownRenderer} onTick={({ total }) => handleTick(total)} overtime={true} /> </span> <i className='fas fa-clock' />
                                                    </>
                                                    :
                                                    <>
                                                        <span className='spec-value' dangerouslySetInnerHTML={{ __html: props.AuctionBiddingData?.TimeLeft }} /> <i className='fas fa-clock' />
                                                    </>
                                                }
                                            </div>
                                            :
                                            props.AuctionBiddingData?.IsAllBiddingPaused || props.AuctionBiddingData?.IsListingPaused ?
                                                <div className="bold"><TranslationText tKey='sandhills.tradepubwebsite.mvc.auctionpaused' /></div>
                                                :
                                                <div>&nbsp;</div>}
                                    </>}
                                {props.IsEF &&
                                    <>
                                        {(props.IsBuyersPremium == true && props.AuctionBiddingData && !showPreBidBox()) &&
                                            <span className='spec-value bold red-text buyers-premium-link' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); getBuyersPremiumData(props.Id, props.CurrencyCode); } }} onClick={() => getBuyersPremiumData(props.Id, props.CurrencyCode)}><TranslationText tKey='Sandhills.ViewBuyersPremium' /></span>}

                                        {props.AuctionBiddingData?.TimeLeft &&
                                            <>
                                                <span className='spec-label'><TranslationText tKey='Sandhills.TimeRemaining' />:</span>
                                                <span className='spec-value' dangerouslySetInnerHTML={{ __html: props.AuctionBiddingData?.TimeLeft }} /> <i className='fas fa-clock' />
                                            </>}
                                    </>}
                            </>}

                        {props.ShippingEstimateData && props.ShippingEstimateData.EstimateDisplay !== null && !props.IsSimilar &&
                            <ShippingEstimate {...props.ShippingEstimateData} blockName='list' skipImage />}

                        {(props.ShowPreAuctionTime && !props.ShowWholesalePriceOnly && !props.IsRY) && !props.IsSimilar &&
                            <div className='other-site-data-section pre-at-price'>
                                <div className='other-site-content'>
                                    {!props.IsFTISearch && <div className='spaced-line'>{translator.translate('sandhills.tradepubwebsite.mvc.preauctiontimeprice')}: <span className='no-wrap'>{props.WholeSalePrice}</span></div>}
                                    <div className='spaced-line'>{translator.translate('sandhills.tradepubwebsite.mvc.toauction')}: <span className='no-wrap'>{props.PreAuctionTimeDate}</span></div>
                                </div>
                            </div>}
                        {props.AuctionDate && !props.IsSimilar &&
                            <div>
                                <span className='spec-label date'>{props.AuctionDateLabel}:</span><span className='spec-value'>{props.AuctionDate}</span>
                            </div>}
                        {(props.ShowWholeSale && !props.IsExclusiveWholeSale && !props.IsRY) && !props.IsSimilar && !props.IsFTISearch &&
                            <div className='other-site-data-section wholesale-price'>
                                <div className='other-site-content'>{translator.translate('sandhills.crm.dscrm.wholesaleprice')}: <span className='no-wrap'>{props.WholeSalePrice}</span></div>
                            </div>}
                        {props.PriceGroup && props.PriceGroup.length > 0 && !props.IsSimilar &&
                            <RentalAndLeasePrices PriceGroup={props.PriceGroup} IsRY={props.IsRY} />}
                        {!props.IsSimilar &&
                            <BadgeList Badges={props.Badges} ref={ref} IsRedesign />}

                        {props.FloorPlanUrlModel && !props.IsSimilar && <FloorPlan {...props.FloorPlanUrlModel} IsRedesign />}

                        {props.EstimatedTimeDisplay && props.EstimatedTimeDisplay != "" &&
                            <div>
                                <span className='spec-label date'>{translator.translate('sandhills.estimatedtimeremaining')}:</span>
                                <span className='spec-value'><i className='fas fa-clock' />&nbsp;{props.EstimatedTimeDisplay}</span>
                            </div>}

                    </div>
                </div>
                {isLivestockHayStaw && !props.IsSimilar ?
                    <div className='spec-toggle-container'>
                        {(props.Specs.length > 1 || (priceDesc.length > 0 && props.Specs.length > 0)) &&
                        {
                            ...showExpandedSpecs ?
                                <div>
                                    {
                                        props.Specs && props.Specs.slice(LSMSpecStartIndex, 8).map((l, i) =>
                                            <div key={i} className='list-spec'>
                                                <div className='spec'>
                                                    <span className='spec-label'>{l.Key}:&nbsp;</span>
                                                    <span className='spec-value'>{truncateString(l.Value, props.ListPageSpecMaxLength)}</span>
                                                </div>
                                            </div>)
                                    }

                                    < div className='specs-button' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); setShowExpandedSpecs(false); } }} onClick={() => setShowExpandedSpecs(false)}>
                                        {translator.translate('sandhills.tradesites.mvc.hidespecs')} <i className='fas fa-chevron-up' />
                                    </div>
                                </div>
                                :
                                <div>
                                    <div className='specs-button' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); setShowExpandedSpecs(true); } }} onClick={() => setShowExpandedSpecs(true)}>
                                        {translator.translate('sandhills.tradesites.mvc.viewspecs')} <i className='fas fa-chevron-down' />
                                    </div>
                                </div>
                        }
                        }
                        {props.ListingLocation && <ListingDisplayLocation {...props.ListingLocation} OneLine UseShortDisplay />}
                        {props.DistanceFrom &&
                            <div className='distance-away'>
                                <i className='fas fa-location-dot' /> {props.DistanceFrom}
                            </div>}
                    </div>
                    :
                    <>
                        {!props.IsSimilar &&
                            <div className='specs-container'>
                                {props.Specs && props.Specs.slice(0, props.TopOutputSpecsCount).map((l, i) =>
                                    <div key={i} className='list-spec'>
                                        <div className='spec'>
                                            <span className='spec-label'>{l.Key}:&nbsp;</span>
                                            <span className='spec-value'>{truncateString(l.Value, props.ListPageSpecMaxLength)}</span>
                                        </div>
                                    </div>)}
                                {showExpandedSpecs &&
                                    <>
                                        {props.Specs.slice(props.TopOutputSpecsCount, maxSpecs).map((l, i) =>
                                            <div key={i} className='list-spec'>
                                                <div className='spec'>
                                                    <span className='spec-label'>{l.Key}:&nbsp;</span>
                                                    <span className='spec-value'>{truncateString(l.Value, props.ListPageSpecMaxLength)}</span>
                                                </div>
                                            </div>)}
                                        {props.Description &&
                                            <>
                                                <div key={'desc'} className='list-spec'>
                                                    <div className='spec'>
                                                        <span className='spec-label'>{translator.translate('sandhills.description')}:&nbsp;</span>
                                                        <span className='spec-value'>{truncateString(props.Description, descriptionCharCount)}&nbsp;</span>
                                                {props.Description.length > descriptionDefaultCharCount &&
                                                    <> {showExpandedDescription ?
                                                        <>
                                                            <a className='more spec-value' href={props.DetailUrl} target={props.openDetailPageInNewTab ? "_blank" : "_self"} aria-label={props.openDetailPageInNewTab ? translator.translate('sandhills.seemoredetails') + " (" + translator.translate('sandhills.opensinanewtab') + ")" : translator.translate('sandhills.seemoredetails')}>
                                                                <TranslationText tKey='sandhills.seemoredetails' />
                                                            </a>
                                                        </> :
                                                        <span className='read-more spec-value' onClick={handleDescriptionToggle}>{translator.translate('sandhills.readmore')}</span>}

                                                    </>}
                                                    </div>
                                                </div>
                                                <br />
                                            </>
                                    }
                                        {props.FormattedAddedOnTime &&
                                        <div key={'added'} className='list-spec'>
                                            <div className='spec'>
                                                    <span className='spec-label'>
                                                        <TranslationText tKey='sandhills.added' />:&nbsp;
                                                    </span>
                                                <span className='spec-value'>{props.FormattedAddedOnTime}</span>&nbsp;&nbsp;
                                            </div>
                                        </div>
                                        }
                                        {props.FormattedUpdatedOnTime &&
                                            <div key={'updated'} className='list-spec'>
                                                <div className='spec'>
                                                    <span className='spec-label'>{translator.translate('sandhills.updated')}:&nbsp;</span>
                                                    <span className='spec-value'>{props.FormattedUpdatedOnTime}</span>
                                                </div>
                                            </div>
                                        }
                                    </>
                                }
                                <div className='specs-button' aria-label={showExpandedSpecs ? translator.translate('sandhills.tradesites.mvc.viewspecs') : translator.translate('sandhills.tradesites.mvc.hidespecs')} role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); setShowExpandedSpecs(!showExpandedSpecs); } }} onClick={() => setShowExpandedSpecs(!showExpandedSpecs)}><i className={showExpandedSpecs ? 'fas fa-angle-up' : 'fas fa-angle-down'} /></div>
                                <span className='hidden'>
                                    {props.Specs.slice(props.TopOutputSpecsCount, maxSpecs).map((l, i) =>
                                        <div key={i} className='list-spec'>
                                            <div className='spec'>
                                                <span className='spec-label'>{l.Key}:&nbsp;</span>
                                                <span className='spec-value'>{truncateString(l.Value, props.ListPageSpecMaxLength)}</span>
                                            </div>
                                        </div>)}
                                    <div key={'desc'} className='list-spec'>
                                        <div className='spec'>
                                            <span className='spec-label'>{translator.translate('sandhills.description')}:&nbsp;</span>
                                            <span className='spec-value'>{truncateString(props.Description, descriptionExpandedCharCount)}</span>
                                        </div>
                                    </div>
                                    <br />
                                    <div key={'updated'} className='list-spec'>
                                        <div className='spec'>
                                            <span className='spec-label'>{translator.translate('sandhills.updated')}:&nbsp;</span>
                                            <span className='spec-value'>{props.FormattedUpdatedOnTime}</span>
                                        </div>
                                    </div>
                                </span>
                                {props.ListingLocation && <ListingDisplayLocation {...props.ListingLocation} OneLine UseShortDisplay />}
                                {props.DistanceFrom &&
                                    <div className='distance-away'>
                                        <i className='fas fa-location-dot' /> {props.DistanceFrom}
                                    </div>}
                            </div>
                        }
                    </>
                }
                {!props.IsSimilar &&
                    <div className='contact-container'>
                        {(hasSAM || !noPhone) &&
                            <div className='shared'>
                                {hasSAM &&
                                    <>
                                        {props.ShowCheckAvailability ?
                                            <div className={noPhone ? 'email full-width' : 'email'} role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); openEmailModal(inquiryType.CheckAvailability); } }} onClick={() => openEmailModal(inquiryType.CheckAvailability)}>
                                                <i className='fas fa-check' /> <TranslationText tKey='sandhills.checkavailability' />
                                            </div>
                                            :
                                            <div className={noPhone ? 'email full-width' : 'email'} ref={ref} role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); openEmailModal(inquiryType.Email); } }} onClick={() => openEmailModal(inquiryType.Email)}>
                                                <i className='fas fa-envelope' /> {props.CustomEmailSellerTextForRental ? props.CustomEmailSellerTextForRental : props.CustomEmailSellerTextForRetail ? props.CustomEmailSellerTextForRetail : (props.IsForRentOnly || props.IsRY) ? <TranslationText tKey='sandhills.email' /> : <TranslationText tKey='sandhills.emailseller' />}
                                            </div>
                                        }
                                    </>}
                                {props.ShowWholeSale && props.IsExclusiveWholeSale && props.WholeSalePhone && props.WholeSalePhoneUnformatted && !props.RetailPhoneNumber && !props.IsPrivateParty &&
                                    <div className={!hasSAM ? 'full-width dealer-data exclusive-wholesale-phone' : 'dealer-data exclusive-wholesale-phone'}>
                                        <i className='fas fa-phone-alt' />&nbsp;
                                        <a href={'tel:' + props.WholeSalePhoneUnformatted} data-phonetype={props.WholeSalePhoneType} className=' phone-link' onClick={(e) => { phoneClickToTrack(e, props.WholeSalePhoneUnformatted, props.WholeSalePhoneType); handleTelClick(phoneType.Wholesale); }}>
                                            <strong>{props.WholeSalePhone}</strong>
                                        </a>
                                    </div>}
                                {props.RetailPhoneNumber && props.ShowDealerPhoneNumber && !props.ShowWholesaleContactInfoOnly && !props.IsForRentOnly && !props.IsRY &&
                                    <div className={!hasSAM ? 'full-width dealer-data' : 'dealer-data'}>
                                        <i className='fas fa-phone-alt' />&nbsp;
                                        <a href={'tel:' + props.RetailPhoneNumberUnformatted} data-phonetype='Retail Phone' className=' phone-link' onClick={(e) => { phoneClickToTrack(e, props.RetailPhoneNumberUnformatted, 'Retail Phone'); handleTelClick(); }}>
                                            <strong>{props.RetailPhoneNumber}</strong>
                                        </a>
                                    </div>}
                                {((props.ShowWholeSale || props.PreAuctionTimeDate) && props.ShowDealerPhoneNumber && !props.IsExclusiveWholeSale) && !props.IsRY && !props.RetailPhoneNumber &&
                                    <div className={!hasSAM ? 'full-width dealer-data wholesale-phone' : 'dealer-data wholesale-phone'}>
                                        <i className='fas fa-phone-alt' />&nbsp;
                                        <a href={'tel:' + props.WholeSalePhoneUnformatted} data-phonetype={props.WholeSalePhoneType} className=' phone-link' onClick={(e) => { phoneClickToTrack(e, props.WholeSalePhoneUnformatted, 'Wholesale Phone'); handleTelClick(phoneType.Wholesale); }}>
                                            <span><strong>{props.WholeSalePhone}</strong></span>
                                        </a>
                                    </div>}
                                {props.RentalContactNumber && (!props.RetailPhoneNumber || props.IsRY || props.IsForRentOnly) &&
                                    <div className={!hasSAM ? 'full-width dealer-data rental-phone' : 'dealer-data rental-phone'}>
                                        <i className='fas fa-phone-alt' />&nbsp;
                                        <a href={'tel:' + props.RentalContactNumber} className=' phone-link' onClick={(e) => { phoneClickToTrack(e, props.RentalContactNumber, 'Rental Phone'); handleTelClick(phoneType.Rental); }}>
                                            <span ><strong>{props.RentalContactNumber}</strong></span>
                                        </a>
                                    </div>}
                            </div>}
                        <div className='seller'>
                            <TranslationText tKey='sandhills.seller' />: {props.WebSite ?
                                <a href={props.WebSite} target='_blank' rel='noopener' aria-label={props.Dealer + " (" + translator.translate('sandhills.opensinanewtab') + ")"}>{props.Dealer}</a> : props.Dealer}
                        </div>
                    </div>
                }
            </div >
        </div >
    );
};

export default WebListing_GridCard;